import React, { useState } from 'react';
import {
  Text,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  makeStyles,
  shorthands,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import {
  MoreHorizontal20Regular,
  Open16Regular,
  Delete16Regular,
} from '@fluentui/react-icons';
import { ConnectorIcon } from '../../utils/connectorIcons';
import { formatRelativeTime } from '../../utils/relativeTime';
import type { ConnectionListItem } from '../../api/types';
import { MutationOnly, useWorkspaceAccess } from '../security/WorkspaceAccess';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    cursor: 'pointer',
    transitionProperty: 'box-shadow, border-color, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'ease',
    position: 'relative',
    overflow: 'hidden',
    ':hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
      transform: 'translateY(-1px)',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    ...shorthands.padding('10px', '14px', '8px'),
    flex: '1 1 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
  },
  title: {
    fontSize: '13px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '1 1 auto',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  statusDot: {
    width: '6px',
    height: '6px',
    ...shorthands.borderRadius('50%'),
    flexShrink: 0,
  },
  statusText: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  meta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
  },
  footer: {
    ...shorthands.padding('6px', '14px', '8px'),
    borderTop: '1px solid rgba(0,0,0,0.04)',
  },
  timestamp: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
  },
  actionsButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: '150ms',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('6px'),
  },
  actionsVisible: {
    opacity: 1,
  },
});

interface StatusConfig {
  color: string;
  label: string;
}

function getStatusConfig(status: string): StatusConfig {
  switch (status) {
    case 'ready':
    case 'connected':
      return { color: '#34d399', label: status === 'ready' ? 'Ready' : 'Synced' };
    case 'error':
    case 'failed':
      return { color: '#ef4444', label: 'Failed' };
    case 'pending':
      return { color: '#f59e0b', label: 'Pending' };
    case 'syncing':
      return { color: '#3b82f6', label: 'Syncing' };
    default:
      return { color: '#94a3b8', label: status || 'Draft' };
  }
}

interface DataSourceCardProps {
  item: ConnectionListItem;
  onClick: () => void;
  onDelete?: (id: string) => void;
}

const DataSourceCard: React.FC<DataSourceCardProps> = ({ item, onClick, onDelete }) => {
  const styles = useStyles();
  const { runMutation } = useWorkspaceAccess();
  const [hovered, setHovered] = useState(false);

  const lastSync = item.lastSyncAt ? new Date(item.lastSyncAt) : null;
  const updatedAt = new Date(item.updatedAt);
  const statusConfig = getStatusConfig(item.status);

  return (
    <div
      className={styles.card}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={styles.content}>
        {/* Header: icon + title */}
        <div className={styles.header}>
          <ConnectorIcon connectorId={item.connectorId} name={item.connector?.name ?? ''} size={20} />
          <Text className={styles.title}>{item.name}</Text>
        </div>

        {/* Status */}
        <div className={styles.statusRow}>
          <span className={styles.statusDot} style={{ backgroundColor: statusConfig.color }} />
          <Text className={styles.statusText}>{statusConfig.label}</Text>
        </div>

        {/* Metadata */}
        <Text className={styles.meta}>
          {item.connector?.name ?? 'Unknown'} • {item.datasetCount ?? 0} {(item.datasetCount ?? 0) === 1 ? 'table' : 'tables'}
        </Text>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <Text className={styles.timestamp}>
          {lastSync ? `Last sync ${formatRelativeTime(lastSync)}` : `Updated ${formatRelativeTime(updatedAt)}`}
        </Text>
      </div>

      {/* Actions menu */}
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button
            appearance="subtle"
            icon={<MoreHorizontal20Regular />}
            size="small"
            className={mergeClasses(styles.actionsButton, hovered && styles.actionsVisible)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Data source actions"
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<Open16Regular />} onClick={(e) => { e.stopPropagation(); onClick(); }}>Open</MenuItem>
            {onDelete && (
              <MutationOnly>
                <MenuItem icon={<Delete16Regular />} onClick={(e) => { e.stopPropagation(); runMutation(() => onDelete(item.id)); }}>Delete</MenuItem>
              </MutationOnly>
            )}
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
};

export default DataSourceCard;